import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import AdminClient from "../../services/dashboard/AdminClient";
import RoleClient from "../../services/dashboard/RoleClient";
import AdminFormModal from "./AdminFormModal";
import AssignRolesModal from "./AssignRolesModal";
import { adminUpdateCurrent } from "../../features/adminAuthSlice";
import toast from "react-hot-toast";
import { Pencil, Users, ShieldAlert, Trash2, Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * AdminUsers — polished admin user management table
 *
 * Features:
 * - Search, role filter, sorting, pagination
 * - Avatars from initials
 * - Action buttons with icons (Edit / Roles / Delete) and permissions checks
 * - Uses Tailwind theme tokens you provided (primary, surface, text, font-nunito)
 *
 * Requires: AdminClient.getAll(), AdminClient.delete(adminId),
 * RoleClient.getAll(), AdminFormModal, AssignRolesModal
 */

function initials(name = "") {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length === 0) return "A";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function AdminUsers() {
  const dispatch = useDispatch();
  const { permissions, admin: currentAdmin } = useSelector((s) => s.adminAuth ?? { permissions: [], admin: null });

  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // ui
  const [showForm, setShowForm] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showAssign, setShowAssign] = useState(false);

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(0);
  const [size] = useState(12);
  const [sortKey, setSortKey] = useState("adminId");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    fetchRoles();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await AdminClient.getAll({ page, size }); // if your client accepts query
      const data = res.data?.responseData ?? res.data ?? res;
      const list = Array.isArray(data) ? data : data.content ?? data.items ?? data;
      setAdmins(list || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load admin users");
    } finally {
      setLoading(false);
    }
  }

  async function fetchRoles() {
    try {
      const res = await RoleClient.getAll();
      const data = res.data?.responseData ?? res.data ?? res;
      const list = Array.isArray(data) ? data : data.content ?? data;
      setRoles(list || []);
    } catch (e) {
      console.error(e);
    }
  }

  function openCreate() {
    setSelectedAdmin(null);
    setShowForm(true);
  }
  function openEdit(admin) {
    setSelectedAdmin(admin);
    setShowForm(true);
  }
  function openAssign(admin) {
    setSelectedAdmin(admin);
    setShowAssign(true);
  }

  async function handleDelete(admin) {
    if (!window.confirm(`Delete admin "${admin.name}"? This cannot be undone.`)) return;
    try {
      await AdminClient.deleteAdmin(admin.adminId);
      toast.success("Admin deleted");
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete admin");
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    return (admins || []).filter((a) => {
      if (!a) return false;
      if (roleFilter && !(a.roles || []).some((r) => r.name === roleFilter)) return false;
      if (!q) return true;
      return (
        String(a.name ?? "").toLowerCase().includes(q) ||
        String(a.username ?? "").toLowerCase().includes(q) ||
        String(a.email ?? "").toLowerCase().includes(q) ||
        (a.roles || []).some((r) => (r.name || "").toLowerCase().includes(q))
      );
    }).sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av === bv) return 0;
      if (sortDir === "asc") return av > bv ? 1 : -1;
      return av > bv ? -1 : 1;
    });
  }, [admins, query, roleFilter, sortKey, sortDir]);

  // Helper to get role badge color
  function getRoleBadgeColor(roleName) {
    const name = (roleName || "").toLowerCase();
    if (name.includes("super")) return "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200/80 dark:border-purple-700/50";
    if (name.includes("admin")) return "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border border-violet-200/80 dark:border-violet-700/50";
    if (name.includes("editor")) return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-700/50";
    return "bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border border-gray-200/80 dark:border-gray-600/50";
  }

  return (
    <div className="p-6 font-nunito bg-gray-50 dark:bg-gray-900 min-h-screen space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Admin Users</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage admin accounts, assign roles and permissions.</p>
      </div>

      {/* Controls Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search admins..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200"
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all duration-200"
        >
          <option value="">All roles</option>
          {roles.map((r) => (
            <option key={r.id ?? r.roleId ?? r.name} value={r.name}>
              {r.name}
            </option>
          ))}
        </select>

        {permissions?.includes("ADMIN_MANAGE") && (
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white shadow-md hover:shadow-lg text-sm font-medium transition-all duration-200 hover:-translate-y-0.5"
          >
            <Plus size={16} /> Create Admin
          </button>
        )}
      </div>

      {/* Card-Style Table Container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-200/80 dark:border-gray-700/80 bg-gray-50/50 dark:bg-gray-900/50">
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort("adminId")}
                    className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                  >
                    ID
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort("name")}
                    className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                  >
                    Admin
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort("username")}
                    className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                  >
                    Username
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                    Roles
                  </span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                    Created
                  </span>
                </th>
                <th className="px-4 py-3 text-right">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600 dark:text-gray-400">
                    Actions
                  </span>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-4"><div className="h-4 w-8 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                    <td className="px-4 py-4 text-right"><div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded inline-block" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Users size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No admins found.</p>
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr
                    key={a.adminId}
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-all duration-150"
                  >
                    <td className="px-4 py-3.5">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">#{a.adminId}</span>
                    </td>

                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/50 text-blue-700 dark:text-blue-300 flex items-center justify-center font-semibold shadow-sm">
                          <span className="text-xs">{initials(a.name)}</span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">{a.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{a.email}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{a.username}</span>
                    </td>

                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5 flex-wrap items-center">
                        {(a.roles || []).length === 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 border border-gray-200/80 dark:border-gray-600/50">
                            No roles
                          </span>
                        ) : (
                          (a.roles || []).map((r) => (
                            <span
                              key={r.id ?? r.name}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-medium shadow-sm ${getRoleBadgeColor(r.name)}`}
                              title={r.description ?? r.name}
                            >
                              {r.name}
                            </span>
                          ))
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(a.creationDate ?? a.creationDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </td>

                    <td className="px-4 py-3.5 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {permissions?.includes("ADMIN_MANAGE") && (
                          <button
                            onClick={() => openEdit(a)}
                            title="Edit admin"
                            className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 transition-all duration-150"
                          >
                            <Pencil size={16} />
                          </button>
                        )}

                        {permissions?.includes("ROLE_MANAGE") && (
                          <button
                            onClick={() => openAssign(a)}
                            title="Assign roles"
                            className="p-2 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 dark:hover:text-purple-400 transition-all duration-150"
                          >
                            <ShieldAlert size={16} />
                          </button>
                        )}

                        {permissions?.includes("ADMIN_MANAGE") && (
                          <button
                            onClick={() => handleDelete(a)}
                            title="Delete admin"
                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-all duration-150"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing <span className="font-medium text-gray-900 dark:text-white">{filtered.length}</span> {filtered.length === 1 ? 'admin' : 'admins'}
        </div>

        <div className="flex items-center gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-150"
            title="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[80px] text-center">
            Page {page + 1}
          </div>

          <button
            onClick={() => setPage((p) => p + 1)}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all duration-150"
            title="Next page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Modals */}
      {showForm && (
        <AdminFormModal
          admin={selectedAdmin}
          roles={roles}
          onClose={(saved) => {
            setShowForm(false);
            fetchData();
            if (saved && saved.adminId === currentAdmin?.adminId) {
              dispatch(adminUpdateCurrent({ admin: saved }));
            }
          }}
        />
      )}

      {showAssign && (
        <AssignRolesModal
          admin={selectedAdmin}
          roles={roles}
          onClose={(updatedForCurrent) => {
            setShowAssign(false);
            fetchData();
            if (updatedForCurrent && updatedForCurrent.adminId === currentAdmin?.adminId) {
              dispatch(adminUpdateCurrent({
                admin: updatedForCurrent,
                roles: (updatedForCurrent.roles || []).map(r => r.name),
                permissions: (updatedForCurrent.roles || []).flatMap(r => r.permissions?.map(p => p.code) || []),
              }));
            }
          }}
        />
      )}
    </div>
  );
}
